import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BaseButton,
  mapJobStatusForHeader,
  useAuth,
  useIsMobile,
  useT,
} from '@repo/common';
import JobListHeader from '../../JobListHeader';
import CardOptionList from '../CardOptionList';
import MobileDetailOptionSheet from './MobileDetailOptionSheet';

import { ReactComponent as NavLeftIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as GraphIcon } from '@repo/common/assets/icons/16px/icon_16px_graph-analytics_outline.svg';
import { ReactComponent as EditIcon } from '@repo/common/assets/icons/16px/icon_16px_edit_outline.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';

const DetailHeader = ({ job, onAction }) => {
  const t = useT();
  const navigate = useNavigate();
  const [optionAnchor, setOptionAnchor] = useState(null);
  const [isOptionSheetOpen, setIsOptionSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const optionRef = useRef(null);

  const { id, status } = job;
  const { user } = useAuth();
  const userRole = user?.role ?? null;
  const normalizedRole = String(userRole ?? '').toUpperCase();
  const isEmployer = [
    'E',
    'ROLE_E',
    'EMPLOYER',
    'ROLE_EMPLOYER',
  ].includes(normalizedRole);
  const isManager =
    isEmployer ||
    ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'].includes(normalizedRole);

  const headerStatus = mapJobStatusForHeader(status);

  const handleOptionClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setOptionAnchor(rect);
  };

  const handleOptionClose = () => {
    setOptionAnchor(null);
  };

  const handleOptionAction = (action) => {
    if (action === 'edit') {
      navigate(`/jobs/admin/${id}/edit`);
      return;
    }

    onAction?.(action);
  };

  const listHeader = (
    <JobListHeader
      status={headerStatus}
      isAdmin={isManager}
      hideArchive={isEmployer}
      isDetail
      forceShowActions
      onDeleteSelected={() => onAction('delete')}
      onHideSelected={() => onAction('hide')}
      onUnhideSelected={() => onAction('unhide')}
      onDeletePermanentSelected={() => onAction('deletePermanent')}
    />
  );

  /* 모바일: 흰 상단 바(뒤로 + 상태별 액션 아이콘 | Edit + ⋮ 바텀시트), 통계 버튼은 노출하지 않는다 */
  if (isMobile) {
    return (
      <div className='sticky top-0 z-30 flex w-full max-w-[868px] items-center justify-between overflow-clip rounded-lg bg-grayscale-gray00 px-4 py-3'>
        <div className='flex items-center self-stretch'>
          <button
            type='button'
            onClick={() => navigate(-1)}
            aria-label={t('Go back', '뒤로 가기')}
            className='flex h-full shrink-0 items-center pr-2 text-icon-primary'
          >
            <NavLeftIcon />
          </button>
          {listHeader}
        </div>

        <div className='flex items-center gap-2'>
          <BaseButton
            type='text_basic'
            size='s'
            icon={<EditIcon />}
            bgclass='pl-2'
            width='w-fit'
            onClick={() => navigate(`/jobs/admin/${id}/edit`)}
          >
            {t('Edit', '수정')}
          </BaseButton>
          <button
            type='button'
            aria-label={t('More options', '더보기')}
            onClick={() => setIsOptionSheetOpen(true)}
            className='flex w-[26px] items-center justify-center self-stretch text-icon-placeholder active:text-icon-primary'
          >
            <OptionIcon className='size-5' />
          </button>
        </div>

        {isOptionSheetOpen && (
          <MobileDetailOptionSheet
            status={status}
            onAction={handleOptionAction}
            onClose={() => setIsOptionSheetOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    // 데스크탑 로고 GNB(74px) 바로 아래에 붙는다
    <div className='sticky top-[74px] z-30 flex items-center justify-between pb-6 gap-2.5 w-[868px] max-w-[868px] bg-grayscale-gray00'>
      <div className='flex items-center'>
        <BaseButton
          type='text'
          size='s'
          onClick={() => navigate(-1)}
          className='h-auto min-w-0 shrink-0 rounded-none border-0 bg-transparent p-0 pr-[3px] hover:bg-transparent cursor-pointer'
          aria-label={t('Go back', '뒤로 가기')}
          icon={<NavLeftIcon />}
        />

        {listHeader}
      </div>

      <div className='flex items-center gap-2'>
        <BaseButton type='text_basic' icon={<GraphIcon />} bgclass='pl-2'>
          {t('statistics', '통계')}
        </BaseButton>
        <BaseButton
          type='text_basic'
          icon={<EditIcon />}
          bgclass='pl-2'
          width='w-fit'
          onClick={() => navigate(`/jobs/admin/${job.id}/edit`)}
        >
          {t('Edit', '수정')}
        </BaseButton>

        <div className='flex items-center gap-2'>
          <OptionIcon
            ref={optionRef}
            className='w-[38px] cursor-pointer text-icon-placeholder hover:text-icon-primary'
            onClick={handleOptionClick}
          />

          {optionAnchor && (
            <CardOptionList
              anchorRect={optionAnchor}
              status={job.status}
              onAction={handleOptionAction}
              onClose={handleOptionClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailHeader;
